import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User } from '../types';
import { api } from '../utils/api';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<{ token: string; user: string }>;
  logout: () => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  console.log('AuthProvider: State', { user, loading });

  useEffect(() => {
    console.log('AuthProvider: useEffect - checking localStorage');
    const token = localStorage.getItem('auth-token');
    const username = localStorage.getItem('auth-username');

    if (token && username) {
      console.log('AuthProvider: Found stored credentials', { username, token: token.substring(0, 10) + '...' });
      setUser({ username, token });
    } else {
      console.log('AuthProvider: No stored credentials found');
    }
    setLoading(false);
  }, []);

  const login = async (username: string, password: string) => {
    console.log('AuthProvider: Starting login attempt', { username });

    try {
      const response = await api.login(username, password);
      console.log('AuthProvider: API login successful', { token: response.token, user: response.user });

      localStorage.setItem('auth-token', response.token);
      localStorage.setItem('auth-username', response.user);
      console.log('AuthProvider: Stored tokens in localStorage');

      const newUser = { username: response.user, token: response.token };
      setUser(newUser);
      console.log('AuthProvider: Set user state', newUser);

      return response;
    } catch (error) {
      console.error('AuthProvider: Login failed', error);
      throw error;
    }
  };

  const logout = () => {
    console.log('AuthProvider: Logging out');
    localStorage.removeItem('auth-token');
    localStorage.removeItem('auth-username');
    setUser(null);
  };

  const value = {
    user,
    loading,
    login,
    logout,
    isAuthenticated: !!user,
  };

  console.log('AuthProvider: Providing context value', value);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}